import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { ApiError, HttpService } from '../../shared/services/http.service';

@Component({
  selector: 'app-verification',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule]
})
export class ResetPasswordComponent implements OnInit {
  @ViewChild('backgroundVideo', { static: false }) backgroundVideo!: ElementRef<HTMLVideoElement>;
  verificationForm!: FormGroup;
  passwordForm!: FormGroup;
  isVerified: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  isInvalidOtp: boolean = false;
  errorMessage: string = '';
  submitted: boolean = false;
  isLoading: boolean = false;
  email: string = '';
  token: string = '';
  isTokenValid: boolean = false;

  digits = ['digit1', 'digit2', 'digit3', 'digit4', 'digit5', 'digit6'];

  @ViewChildren('inputRefs') codeInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
  ) {
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    const storedVerification = localStorage.getItem('isVerified');
    this.isVerified = storedVerification === 'true';
    this.initVerificationForm();
    this.initPasswordForm();

    this.route.queryParams.subscribe(params => {
      this.email = params['email'];
      this.token = params['token'];
      if (this.email && this.token) {
        this.getValidity();
      } else {
        this.router.navigate(['/unauthorized']);
      }
    });
  }

  getValidity(): void {
    this.isLoading = true;
    const encodedToken = encodeURIComponent(this.token);
    this.httpService.get<any>(
      `auth/validate-reset-token?email=${encodeURIComponent(this.email)}&token=${encodedToken}`).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response?.status) {
            this.isTokenValid = true;
            this.isVerified = true;
            localStorage.setItem('isVerified', 'true');
          } else {
            this.isTokenValid = false;
            this.router.navigate(['/unauthorized']);
            this.showErrorToast(response?.message || 'Something went wrong. Please try again later.');
          }
        },
        error: (error: ApiError) => {
          this.isLoading = false;
          this.isTokenValid = false;
          this.router.navigate(['/unauthorized']);
          this.showErrorToast(error.message || 'Something went wrong. Please try again later.');
        },
      });
  }

  initVerificationForm(): void {
    this.verificationForm = this.fb.group(
      Object.fromEntries(this.digits.map(d => [d, ['', [Validators.required, Validators.pattern('[0-9]')]]]))
    );
  }

  initPasswordForm(): void {
    this.passwordForm = this.fb.group({
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/)
      ]],
      confirmPassword: ['', Validators.required]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  preventSpace(event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space') {
      event.preventDefault();
    }
  }

  preventPasteWithSpace(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text');
    if (pastedText) {
      // Remove all spaces from pasted text
      const textWithoutSpaces = pastedText.replace(/\s/g, '');

      // Insert the text without spaces at cursor position
      const input = event.target as HTMLInputElement;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;
      const newValue = currentValue.substring(0, start) + textWithoutSpaces + currentValue.substring(end);

      // Update form control and input value
      input.value = newValue;
      const formControlName = input.getAttribute('formControlName');
      if (formControlName) {
        this.passwordForm.get(formControlName)?.setValue(newValue);
      }

      // Set cursor position after pasted text
      input.setSelectionRange(start + textWithoutSpaces.length, start + textWithoutSpaces.length);
    }
  }

  getPasswordErrorMessage(): string {
    const passwordControl = this.passwordForm.get('password');

    if (!passwordControl?.touched || !passwordControl?.invalid) {
      return '';
    }

    if (passwordControl.hasError('required')) {
      return 'Password is required';
    }

    if (passwordControl.hasError('minlength')) {
      return 'Password must be at least 8 characters';
    }

    // For pattern error, check which specific criteria are missing
    if (passwordControl.hasError('pattern')) {
      const passwordValue = passwordControl.value || '';
      const missingCriteria = [];

      // Check for uppercase
      if (!/(?=.*[A-Z])/.test(passwordValue)) {
        missingCriteria.push('uppercase');
      }

      // Check for lowercase
      if (!/(?=.*[a-z])/.test(passwordValue)) {
        missingCriteria.push('lowercase');
      }

      // Check for numbers
      if (!/(?=.*\d)/.test(passwordValue)) {
        missingCriteria.push('number');
      }

      // Check for special characters
      if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(passwordValue)) {
        missingCriteria.push('special character');
      }

      if (missingCriteria.length > 0) {
        return `Password must include ${missingCriteria.join(', ')}`;
      }
    }

    return '';
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    if (value && !/^\d+$/.test(value)) {
      input.value = '';
      this.verificationForm.get(this.digits[index])?.setValue('');
      return;
    }
    if (value && index < this.digits.length - 1) {
      this.codeInputs.toArray()[index + 1].nativeElement.focus();
    }
    if (value.length > 1) {
      input.value = value[0];
      this.verificationForm.get(this.digits[index])?.setValue(value[0]);
    }
    this.isInvalidOtp = false;
    this.errorMessage = '';
  }

  onOtpKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace') {
      if (input.value === '') {
        if (index > 0) {
          const prevInput = this.codeInputs.toArray()[index - 1].nativeElement;
          prevInput.focus();
          prevInput.value = '';
          this.verificationForm.get(this.digits[index - 1])?.setValue('');
          event.preventDefault();
        }
      } else {
        input.value = '';
        this.verificationForm.get(this.digits[index])?.setValue('');
        event.preventDefault();
      }
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      this.codeInputs.toArray()[index - 1].nativeElement.focus();
      event.preventDefault();
    }
    if (event.key === 'ArrowRight' && index < this.digits.length - 1) {
      this.codeInputs.toArray()[index + 1].nativeElement.focus();
      event.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text').replace(/\D/g, '');
    if (pastedText.length === this.digits.length) {
      const inputs = this.codeInputs.toArray();
      this.digits.forEach((d, i) => {
        this.verificationForm.get(d)?.setValue(pastedText[i]);
        inputs[i].nativeElement.value = pastedText[i];
      });
      setTimeout(() => inputs[this.digits.length - 1].nativeElement.focus(), 0);
    }
  }

  getVerificationCode(): string {
    return Object.values(this.verificationForm.value).join('');
  }

  onResetPassword(): void {
    if (!this.isTokenValid) {
      this.showErrorToast('This reset link has expired. Please request a new one.');
      this.router.navigate(['/forgot-password']);
      return;
    }

    this.submitted = true;
    if (this.passwordForm.valid && !this.isLoading) {
      this.isLoading = true;

      const newPassword = this.passwordForm.get('password')?.value;
      const payload = {
        email: this.email,
        token: this.token,
        newPassword: newPassword
      };

      this.httpService.post<any>('auth/reset-password', payload).subscribe({
        next: (response: any) => {
          this.isLoading = false;

          if (response?.status) {
            this.router.navigate(['auth/login']);
            this.showSuccessToast(response?.message);
            localStorage.removeItem('isVerified');
          } else {
            this.showErrorToast(response?.message || 'Reset Password failed');
          }
        },
        error: (error: ApiError) => {
          this.isLoading = false;
          this.showErrorToast(error.message);
        },
      });
    } else {
      this.passwordForm.markAllAsTouched();
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  resendCode(): void {
    this.isInvalidOtp = false;
    this.errorMessage = '';
  }

  backToLogin(): void {
    localStorage.removeItem('isVerified');
    this.router.navigate(['/login']);
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}